/**
 * Display-only effects (the client may use Math.random here: nothing goes back into the game state).
 *
 * Explosion, in four layers: a white flash at once, a fireball that swells and cools from yellow to red,
 * a shock ring that runs past the radius, then debris in the colours of the crater's materials and a few
 * puffs of smoke that rise and spread. A short screen shake follows the size of the blast.
 */
import { Physique, STATE_LIQUID, type World } from '@invaders/sim';
import { INVADERS, CONTENT_MEDKIT, MEDKIT_HEAL, contentName, type GameEvent, type GameState } from '@invaders/game';
import { cssColor } from './palette';
import type { Camera } from './camera';
import { buildPalette } from './palette';
import { toScreen } from './draw-units';
import { sound } from './sound';

interface Particle { x: number; y: number; vx: number; vy: number; life: number; max: number; color: string; size: number; smoke: boolean; flame?: boolean }
interface Blast { x: number; y: number; r: number; age: number }
interface Tracer { x0: number; y0: number; x1: number; y1: number; age: number }
interface Floater { x: number; y: number; text: string; age: number; color: string }

const FLASH = 0.08;
const FIREBALL = 0.32;
const RING = 0.4;
const GRAVITY = 120; // cells / s²
/** Cells of the view looked at per frame for fire, to send smoke up. */
const SMOKE_SAMPLES = 60;
const MAX_PARTICLES = 900;
/** A unit landing faster than this (fixed-point, 1 cell = 256) raises dust. */
const LANDING_SPEED = 3 * 256;
/** Render frames between two footsteps of a walking unit. */
const STEP_FRAMES = 14;

function abgrToCss(c: number): string {
  return `rgb(${c & 0xff},${(c >> 8) & 0xff},${(c >> 16) & 0xff})`;
}

export class Effects {
  private particles: Particle[] = [];
  private blasts: Blast[] = [];
  private tracers: Tracer[] = [];
  private floaters: Floater[] = [];
  private shake = 0;
  private rings: { x: number; y: number; r: number; age: number; color: string }[] = [];
  private sparks: { cells: number[]; x: number; y: number; r: number; age: number }[] = [];
  private beams: { path: number[]; age: number }[] = [];
  private readonly palette = buildPalette();
  /** Per unit or projectile: in a liquid at the last frame, and its vertical speed (display only). */
  private wet = new WeakMap<object, boolean>();
  private fall = new WeakMap<object, number>();
  /** Frames a unit has been walking on the ground (footsteps). */
  private stride = new WeakMap<object, number>();

  /** Feeds the events of one tick; `unitPos` gives a unit's head position for damage numbers. */
  onEvents(events: readonly GameEvent[], world: World, unitPos: (id: number) => [number, number] | null): void {
    for (const e of events) {
      if (e.type === 'explosion') this.explosion(e.x, e.y, e.radius, world);
      else if (e.type === 'shot') this.tracers.push({ x0: e.x0, y0: e.y0, x1: e.x1, y1: e.y1, age: 0 });
      else if (e.type === 'burst') {
        const color = cssColor(INVADERS[e.kind].color);
        for (let i = 0; i < 40; i++) this.debris(e.x, e.y, 30, i % 3 === 0 ? '#ffffff' : color, 0.5 + Math.random() * 0.6);
      } else if (e.type === 'legacy') {
        const color = e.stage === 0 ? '#9aa0a8' : cssColor(INVADERS[e.kind].color);
        for (let i = 0; i < (e.stage === 0 ? 20 : 30); i++) {
          this.particles.push({ x: e.x + (Math.random() - 0.5) * 8, y: e.y - 4, vx: (Math.random() - 0.5) * 10, vy: -15 - Math.random() * 25, life: 0, max: 0.8, color, size: 1, smoke: false });
        }
        if (e.stage === 0) this.floaters.push({ x: e.x, y: e.y - 12, text: 'legacy cancelled', age: 0, color: '#c8ccd2' });
      } else if (e.type === 'chest') {
        if (e.what === 'open') {
          const text = e.content === CONTENT_MEDKIT ? `+${MEDKIT_HEAL} PV` : `+ ${contentName(e.content)}`;
          this.floaters.push({ x: e.x + 5, y: e.y - 6, text, age: 0, color: '#ffd23f' });
        } else if (e.what === 'lost') this.floaters.push({ x: e.x + 5, y: e.y - 6, text: 'chest lost', age: 0, color: '#ff8a2a' });
        else if (e.what === 'blown') this.floaters.push({ x: e.x + 5, y: e.y - 6, text: contentName(e.content), age: 0, color: '#ffd23f' });
      } else if (e.type === 'surge') {
        for (let i = 0; i < 20 + e.height * 2; i++) this.debris(e.x, e.y - e.height / 2, 20 + e.height, i % 4 === 0 ? '#e8f4ff' : '#4f8fe0', 0.6 + Math.random() * 0.5);
      } else if (e.type === 'freeze') {
        this.rings.push({ x: e.x, y: e.y, r: e.radius, age: 0, color: '200, 240, 255' });
        for (let i = 0; i < 30 + e.radius; i++) {
          const a = Math.random() * Math.PI * 2;
          const d = Math.random() * e.radius;
          this.particles.push({ x: e.x + Math.cos(a) * d, y: e.y + Math.sin(a) * d, vx: (Math.random() - 0.5) * 6, vy: 4 + Math.random() * 6, life: 0, max: 0.9 + Math.random() * 0.6, color: '#eaf8ff', size: 1, smoke: false, flame: true });
        }
      } else if (e.type === 'discharge') {
        this.sparks.push({ cells: e.cells, x: e.x, y: e.y, r: e.radius, age: 0 });
      } else if (e.type === 'meteor') {
        for (let i = 0; i < 40; i++) this.debris(e.x + 7, e.y + 12, 30, i % 2 ? '#a8e4f4' : '#ffffff', 0.5 + Math.random() * 0.4);
        this.shake = Math.max(this.shake, 0.2);
      } else if (e.type === 'laser') {
        this.beams.push({ path: e.path, age: 0 });
      } else if (e.type === 'bite') {
        this.floaters.push({ x: e.x + 5, y: e.y - 6, text: 'Mimic!', age: 0, color: '#ff5050' });
      } else if (e.type === 'remote') {
        for (let i = 0; i < 30; i++) this.debris(e.tx, e.ty, 20, '#ffd23f', 0.4 + Math.random() * 0.3);
      } else if (e.type === 'drill') {
        // A tunnel at once (50 chips along it), or the bit of a unit boring its way (a few chips).
        const n = e.x0 === e.x1 && e.y0 === e.y1 ? 8 : 50;
        for (let i = 0; i < n; i++) {
          const k = Math.random();
          this.debris(e.x0 + (e.x1 - e.x0) * k, e.y0 + (e.y1 - e.y0) * k, 14, '#8c8a84', 0.5 + Math.random() * 0.5);
        }
      } else if (e.type === 'eruption') {
        // The volcano: a roar of lava and smoke out of the crater, and the ground shakes.
        this.shake = Math.max(this.shake, 0.6);
        for (let i = 0; i < 80; i++) this.debris(e.x, e.y - 2, 50, i % 3 === 0 ? '#ffd23f' : '#ff5a1f', 0.6 + Math.random() * 0.8);
        for (let i = 0; i < 14; i++) {
          this.particles.push({ x: e.x + (Math.random() - 0.5) * 30, y: e.y - 6, vx: (Math.random() - 0.5) * 12, vy: -20 - Math.random() * 25, life: 0, max: 2 + Math.random(), color: '#3a3438', size: 6 + Math.random() * 8, smoke: true });
        }
        this.floaters.push({ x: e.x, y: e.y - 24, text: 'Eruption!', age: 0, color: '#ff8a2a' });
      } else if (e.type === 'stoneRain') {
        // The end of the world: the sky starts throwing lava bombs, the ground shakes.
        this.shake = Math.max(this.shake, 0.4);
      } else if (e.type === 'cauldron') {
        for (let i = 0; i < 30; i++) this.debris(e.x, e.y + 4, 16, i % 3 === 0 ? '#ffb02e' : '#5a4a1a', 0.5 + Math.random() * 0.4);
        this.floaters.push({ x: e.x, y: e.y - 8, text: 'Boiling oil!', age: 0, color: '#ffb02e' });
      } else if (e.type === 'damage') {
        const p = unitPos(e.unit);
        if (p) this.floaters.push({ x: p[0], y: p[1], text: `-${e.amount}`, age: 0, color: '#ff5050' });
      }
    }
  }

  private explosion(x: number, y: number, r: number, world: World): void {
    const { w, h, data } = world.physique;
    if (r <= 0) {
      // Sandbag: a puff of sand.
      for (let i = 0; i < 24; i++) this.debris(x, y, 6, '#d6b85a', 0.5);
      return;
    }
    this.blasts.push({ x, y, r, age: 0 });
    this.shake = Math.max(this.shake, Math.min(1, r / 40) * 0.25);
    const n = 30 + r * 2;
    for (let i = 0; i < n; i++) {
      // Colour of a random cell just outside the crater (what was blown away): rock grey, sand yellow...
      const a = Math.random() * Math.PI * 2;
      const d = r * (1.02 + Math.random() * 0.15);
      const cx = Math.round(x + Math.cos(a) * d);
      const cy = Math.round(y + Math.sin(a) * d);
      const mat = cx >= 0 && cx < w && cy >= 0 && cy < h ? data[cy * w + cx] : 0;
      const color = mat === 0 ? (Math.random() < 0.5 ? '#ffb02e' : '#504860') : abgrToCss(this.palette[mat]);
      this.debris(x, y, r * 2.2, color, 0.6 + Math.random() * 0.7);
    }
    for (let i = 0; i < 6 + (r >> 3); i++) {
      const a = Math.random() * Math.PI * 2;
      this.particles.push({
        x: x + Math.cos(a) * r * 0.3, y: y + Math.sin(a) * r * 0.3,
        vx: Math.cos(a) * 8, vy: -10 - Math.random() * 12,
        life: 0, max: 1.2 + Math.random() * 0.8, color: '#4a4a52', size: r * 0.25, smoke: true,
      });
    }
  }

  private debris(x: number, y: number, speed: number, color: string, max: number): void {
    const a = Math.random() * Math.PI * 2;
    const v = speed * (0.3 + Math.random() * 0.7);
    this.particles.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - speed * 0.4, life: 0, max, color, size: 1, smoke: false });
  }

  /** Flamethrower: flame particles along the aim of every jet, each frame (display only). */
  jets(state: GameState): void {
    for (const j of state.jets) {
      const u = state.units[j.unit];
      if (!u) continue;
      const a = (j.angle * Math.PI) / 180;
      const x = u.x / 256 + 8;
      const y = u.y / 256 + 8;
      for (let i = 0; i < 6; i++) {
        const spread = (Math.random() - 0.5) * 0.4;
        const v = 120 + Math.random() * 60;
        this.particles.push({
          x, y, vx: Math.cos(a + spread) * v, vy: -Math.sin(a + spread) * v, life: 0, max: 0.5 + Math.random() * 0.25,
          color: Math.random() < 0.5 ? '#ffb02e' : '#ff5a1f', size: 1 + Math.random(), smoke: false, flame: true,
        });
      }
    }
  }

  /**
   * Particles of the living map, every frame: smoke over the fires (pushed by the wind: the GDD's forest fires),
   * a splash where a unit or a projectile falls into a liquid, dust where a unit lands hard.
   */
  ambient(state: GameState, cam: Camera): void {
    const { w, h, data } = state.world.physique;
    const matState = state.world.rules.materials.state;
    if (!state.map.airless && this.particles.length < MAX_PARTICLES) {
      for (let k = 0; k < SMOKE_SAMPLES; k++) {
        const x = Math.floor(cam.x + Math.random() * cam.spanW);
        const y = Math.floor(cam.y + Math.random() * cam.spanH);
        if (x < 0 || x >= w || y < 1 || y >= h || data[y * w + x] !== Physique.FIRE || Math.random() < 0.5) continue;
        this.particles.push({
          x: x + Math.random(), y: y - 1, vx: state.wind * 1.6 + (Math.random() - 0.5) * 4, vy: -8 - Math.random() * 8,
          life: 0, max: 1.6 + Math.random(), color: Math.random() < 0.5 ? '#3a3638' : '#4a4446', size: 1.2, smoke: true,
        });
      }
    }
    const inLiquid = (x: number, y: number) => x >= 0 && x < w && y >= 0 && y < h && matState[data[y * w + x]] === STATE_LIQUID;
    for (const u of state.units) {
      if (!u.alive) continue;
      const cx = Math.floor(u.x / 256 + 8);
      const feet = Math.floor(u.y / 256 + 15);
      const wet = inLiquid(cx, feet);
      const was = this.wet.get(u);
      if (was === false && wet) this.splashAt(cx, feet, u.vy / 256);
      this.wet.set(u, wet);
      if (u.thrust) {
        // The jetpack's exhaust, down from its back.
        const bx = Math.floor(u.x / 256) + (u.facing > 0 ? 1 : 13);
        for (let k = 0; k < 2; k++) {
          this.particles.push({
            x: bx + Math.random() * 2, y: Math.floor(u.y / 256) + 15, vx: (Math.random() - 0.5) * 10, vy: 40 + Math.random() * 30,
            life: 0, max: 0.25 + Math.random() * 0.2, color: Math.random() < 0.5 ? '#ffb02e' : '#ff5a1f', size: 1, smoke: false,
          });
        }
        sound.jet(bx, Math.floor(u.y / 256) + 15);
      }
      const vy = this.fall.get(u) ?? 0;
      if (vy > LANDING_SPEED && u.vy === 0 && !wet) this.dust(cx, feet + 1);
      // Sounds of the moves: a landing, a jump (a sudden rise without the jetpack), a footstep every few frames.
      if (vy > 256 && u.vy === 0 && !wet) sound.land(vy > LANDING_SPEED, cx, feet);
      if (vy >= 0 && u.vy < -2 * 256 && !u.thrust && !wet) sound.jump(cx, feet);
      const stride = u.walkDir !== 0 && u.vy === 0 && !wet ? (this.stride.get(u) ?? 0) + 1 : 0;
      if (stride % STEP_FRAMES === 1) sound.step(cx, feet);
      this.stride.set(u, stride);
      this.fall.set(u, u.vy);
    }
    for (const p of state.projectiles) {
      const x = Math.floor(p.x / 65536);
      const y = Math.floor(p.y / 65536);
      const wet = inLiquid(x, y);
      if (this.wet.get(p) === false && wet) this.splashAt(x, y, p.vy / 65536);
      this.wet.set(p, wet);
    }
  }

  private splashAt(x: number, y: number, speed: number): void {
    const n = 8 + Math.min(16, Math.floor(Math.abs(speed) * 4));
    for (let i = 0; i < n; i++) {
      this.particles.push({
        x: x + (Math.random() - 0.5) * 6, y: y - 1, vx: (Math.random() - 0.5) * 40, vy: -30 - Math.random() * 50,
        life: 0, max: 0.5 + Math.random() * 0.4, color: Math.random() < 0.3 ? '#e8f4ff' : '#4f8fe0', size: 1, smoke: false,
      });
    }
    sound.splash(x, y);
  }

  private dust(x: number, y: number): void {
    for (let i = 0; i < 10; i++) {
      const dir = i % 2 === 0 ? 1 : -1;
      this.particles.push({
        x: x + dir * 6, y: y - 1, vx: dir * (10 + Math.random() * 20), vy: -4 - Math.random() * 6,
        life: 0, max: 0.6 + Math.random() * 0.3, color: '#8c8478', size: 0.8 + Math.random() * 0.6, smoke: true,
      });
    }
  }

  get busy(): boolean {
    return this.particles.length + this.blasts.length + this.tracers.length + this.floaters.length > 0;
  }

  update(dt: number): void {
    for (const p of this.particles) {
      p.life += dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      if (p.smoke) { p.vx *= 0.96; p.vy *= 0.97; p.size += dt * 6; } else if (p.flame) { p.vx *= 0.94; p.vy = p.vy * 0.94 - 40 * dt; } else p.vy += GRAVITY * dt;
    }
    this.particles = this.particles.filter((p) => p.life < p.max);
    for (const b of this.blasts) b.age += dt;
    this.blasts = this.blasts.filter((b) => b.age < RING);
    for (const t of this.tracers) t.age += dt;
    this.tracers = this.tracers.filter((t) => t.age < 0.18);
    for (const f of this.floaters) { f.age += dt; f.y -= dt * 12; }
    this.floaters = this.floaters.filter((f) => f.age < 1.1);
    this.shake = Math.max(0, this.shake - dt);
    for (const r of this.rings) r.age += dt;
    this.rings = this.rings.filter((r) => r.age < 0.6);
    for (const k of this.sparks) k.age += dt;
    for (const b of this.beams) b.age += dt;
    this.beams = this.beams.filter((b) => b.age < 0.3);
    this.sparks = this.sparks.filter((k) => k.age < 0.5);
  }

  /** Screen-shake offset in canvas pixels, to add to the whole frame. */
  shakeOffset(cam: Camera): [number, number] {
    if (this.shake <= 0) return [0, 0];
    const amp = this.shake * 8 * cam.scale;
    return [(Math.random() - 0.5) * amp, (Math.random() - 0.5) * amp];
  }

  /** Drawn under the units: smoke and fireballs. */
  drawBack(ctx: CanvasRenderingContext2D, cam: Camera): void {
    for (const p of this.particles) {
      if (!p.smoke) continue;
      const [sx, sy] = toScreen(cam, p.x, p.y);
      ctx.globalAlpha = 0.45 * (1 - p.life / p.max);
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(sx, sy, p.size * cam.scale, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    for (const b of this.blasts) {
      const [sx, sy] = toScreen(cam, b.x, b.y);
      const s = cam.scale;
      if (b.age < FIREBALL) {
        const k = b.age / FIREBALL;
        const r = b.r * (0.35 + 0.65 * Math.sqrt(k)) * s;
        const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, r);
        g.addColorStop(0, `rgba(255, 250, 210, ${1 - k})`);
        g.addColorStop(0.45, `rgba(255, ${Math.round(180 - 120 * k)}, 40, ${0.95 - 0.7 * k})`);
        g.addColorStop(1, 'rgba(200, 40, 20, 0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(sx, sy, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  /** Drawn over the units: flash, shock ring, debris, tracers, damage numbers, cold and electricity. */
  drawFront(ctx: CanvasRenderingContext2D, cam: Camera): void {
    const s = cam.scale;
    for (const r of this.rings) {
      const [sx, sy] = toScreen(cam, r.x, r.y);
      const k = r.age / 0.6;
      ctx.fillStyle = `rgba(${r.color}, ${0.35 * (1 - k)})`;
      ctx.beginPath();
      ctx.arc(sx, sy, r.r * (0.4 + 0.6 * Math.min(1, k * 2)) * s, 0, Math.PI * 2);
      ctx.fill();
    }
    // Laser: a red ray along its path (with its bounce on ice), fading.
    for (const b of this.beams) {
      const k = 1 - b.age / 0.3;
      for (const [width, color] of [[4, `rgba(255, 60, 60, ${0.4 * k})`], [1.5, `rgba(255, 220, 220, ${k})`]] as const) {
        ctx.strokeStyle = color;
        ctx.lineWidth = Math.max(1, width * s * 0.5);
        ctx.beginPath();
        for (let i = 0; i < b.path.length; i += 2) {
          const [x, y] = toScreen(cam, b.path[i] + 0.5, b.path[i + 1] + 0.5);
          if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
    }
    // Electricity: jagged bolts from the carcass to electrified cells, flickering.
    for (const k of this.sparks) {
      const [x0, y0] = toScreen(cam, k.x, k.y);
      ctx.strokeStyle = `rgba(255, 250, 140, ${1 - k.age / 0.5})`;
      ctx.lineWidth = Math.max(1, s / 2);
      ctx.beginPath();
      for (let i = 0; i < k.cells.length; i += 2) {
        if (Math.random() < 0.6) continue;
        const [x1, y1] = toScreen(cam, k.cells[i] + 0.5, k.cells[i + 1] + 0.5);
        ctx.moveTo(x1, y1);
        ctx.lineTo(x1 + (Math.random() - 0.5) * 4 * s, y1 + (Math.random() - 0.5) * 4 * s);
      }
      for (let i = 0; i < 6; i++) {
        let px = x0;
        let py = y0;
        ctx.moveTo(px, py);
        const a = Math.random() * Math.PI * 2;
        for (let j = 0; j < 5; j++) {
          px += Math.cos(a) * k.r * 0.3 * s + (Math.random() - 0.5) * 3 * s;
          py += Math.sin(a) * k.r * 0.3 * s + (Math.random() - 0.5) * 3 * s;
          ctx.lineTo(px, py);
        }
      }
      ctx.stroke();
    }
    for (const b of this.blasts) {
      const [sx, sy] = toScreen(cam, b.x, b.y);
      if (b.age < FLASH) {
        ctx.fillStyle = `rgba(255, 255, 255, ${0.9 * (1 - b.age / FLASH)})`;
        ctx.beginPath();
        ctx.arc(sx, sy, b.r * 0.7 * s, 0, Math.PI * 2);
        ctx.fill();
      }
      const k = b.age / RING;
      ctx.strokeStyle = `rgba(255, 240, 200, ${0.7 * (1 - k)})`;
      ctx.lineWidth = Math.max(1, s * (2 - 1.5 * k));
      ctx.beginPath();
      ctx.arc(sx, sy, b.r * (0.6 + 1.0 * k) * s, 0, Math.PI * 2);
      ctx.stroke();
    }
    for (const p of this.particles) {
      if (p.smoke) continue;
      const [sx, sy] = toScreen(cam, p.x, p.y);
      ctx.globalAlpha = Math.min(1, 2 * (1 - p.life / p.max));
      ctx.fillStyle = p.color;
      ctx.fillRect(sx, sy, s * p.size, s * p.size);
    }
    ctx.globalAlpha = 1;
    for (const t of this.tracers) {
      const [x0, y0] = toScreen(cam, t.x0 + 0.5, t.y0 + 0.5);
      const [x1, y1] = toScreen(cam, t.x1 + 0.5, t.y1 + 0.5);
      ctx.strokeStyle = `rgba(255, 230, 120, ${1 - t.age / 0.18})`;
      ctx.lineWidth = Math.max(1, s * 0.6);
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.stroke();
    }
    ctx.textAlign = 'center';
    ctx.font = `bold ${Math.max(12, 4 * s)}px system-ui, sans-serif`;
    for (const f of this.floaters) {
      const [sx, sy] = toScreen(cam, f.x, f.y);
      ctx.globalAlpha = Math.min(1, 2 * (1.1 - f.age));
      ctx.fillStyle = '#000';
      ctx.fillText(f.text, sx + 1, sy + 1);
      ctx.fillStyle = f.color;
      ctx.fillText(f.text, sx, sy);
    }
    ctx.globalAlpha = 1;
  }
}
